import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { capitalize, compact } from 'lodash-es';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { getRepairPalDataPath, ReliabilityEntities, ReliabilityModel } from 'client/data/models/reliability';
import { FeatureSpecsEntities, FeatureSpecsPaths, VehicleModel } from 'client/data/models/vehicle-v2';
import { MaintenanceEntities, MaintenanceModel, MaintenancePaths } from 'client/data/models/maintenance';
import { SCROLL_DURATION, SCROLL_OFFSET } from 'site-modules/shared/constants/sub-navigation';
import { MENU_HASH } from 'site-modules/shared/constants/core-page/menu-hash';
import { getFeatureValue, isAvailable, NOT_AVAILABLE } from 'site-modules/shared/utils/features-specs/utils';
import { getLastWordPlural, getPlural } from 'client/utils/plural';
import { numberWithCommasForString } from 'site-modules/shared/utils/string';
import { getPriceString } from 'site-modules/shared/utils/price-utils';
import { getCompareIconClassName } from 'site-modules/shared/utils/core-page/reliability-ratings';
import { Accordion } from 'site-modules/shared/components/accordion/accordion';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { ContentCollapse } from 'site-modules/shared/components/content-collapse/content-collapse';
import { Link } from 'site-modules/shared/components/link/link';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
import { PopoverItem } from 'site-modules/shared/components/popover-item/popover-item';
import { RatingStars } from 'site-modules/shared/components/rating-stars/rating-stars';

import './reliability.scss';

const CREATIVE_ID = 'reliability';
const SECTION_ID = {
  REPAIR_PAL: 'reliability-repairpal',
  RECALLS: 'reliability-recalls',
  WARRANTY: 'reliability-warranty',
};
const RECALL_SECTIONS = [
  { label: 'Recall Number', key: 'recallNumber', wrapperClassName: 'd-md-flex' },
  { label: 'Manufacturer Recall Number', key: 'manufacturerRecallNumber', wrapperClassName: 'd-md-flex' },
  {
    label: 'Manufactured from',
    key: 'manufacturedFrom',
    connector: ' to ',
    connectedKey: 'manufacturedTo',
    wrapperClassName: 'd-md-flex',
  },
  { label: 'Number of vehicles affected', key: 'numberOfVehiclesAffected', wrapperClassName: 'd-md-flex' },
  { label: 'Defect Description', key: 'defectDescription', wrapperClassName: 'mt-1' },
  { label: 'Defect Consequence', key: 'defectConsequence', wrapperClassName: 'mt-1' },
  { label: 'Corrective Action', key: 'defectCorrectiveAction', wrapperClassName: 'mt-1' },
];

function ReliabilityUI({
  className = null,
  vehicleParams: { makeName, modelName, year },
  reliabilityRatings = null,
  styleFeatures = null,
  recalls = null,
  isUsed = false,
  isMobile = false,
}) {
  const {
    reliabilityScore5Scale,
    repairCost,
    relativeFrequency,
    severity,
    segment,
    segmentRankByReliabilityScore,
    modelCountOfSegment,
    avgSegmentRepairCost,
    avgSegmentRelativeFrequency,
    avgSegmentSeverity,
    gearScoreDescription,
  } = reliabilityRatings || {};

  const recallsCount = recalls?.length;
  const allWarranties = styleFeatures?.features?.standard?.Warranty;
  const basicWarranty = getFeatureValue(styleFeatures, 'features.standard.Warranty', 'Basic', NOT_AVAILABLE);
  const basicWarrantyText = numberWithCommasForString(basicWarranty?.replace('yr.', 'years ')?.replace('mi.', 'miles'));
  const mmySummary = compact([
    isAvailable(basicWarranty) && `a ${basicWarrantyText} basic warranty`,
    `${recallsCount || 'no'} ${getPlural('recall', recallsCount)}`,
  ]).join(' and ');

  const repairPalSections = [
    {
      label: 'Cost',
      tooltipText: `The average total annual cost for unscheduled repairs and maintenance across all model years of the ${makeName} ${modelName} from 2008-2022.`,
      value: `${getPriceString(repairCost)}/yr`,
      compareValue: `${getPriceString(avgSegmentRepairCost)}/yr`,
      iconClassName: getCompareIconClassName(repairCost, avgSegmentRepairCost),
    },
    {
      label: 'Frequency',
      tooltipText:
        'The average number of times this model is brought into the shop for unscheduled repairs and maintenance in a single year. RepairPal calculates this metric by tracking millions of unique vehicles over multiple years to determine an average number of visits per year (omitting small routine visits, e.g., oil changes).',
      value: `${relativeFrequency}x/yr`,
      compareValue: `${avgSegmentRelativeFrequency}x/yr`,
      colClassName: 'separators',
      iconClassName: getCompareIconClassName(relativeFrequency, avgSegmentRelativeFrequency),
    },
    {
      label: 'Severity',
      tooltipText:
        'The probability that a repair will be a major issue, meaning the repair costs 3x the average annual repair cost for all models. This threshold will be higher for vehicles that have higher labor rates and parts costs (such as a premium brand).',
      value: `${severity}%`,
      compareValue: `${avgSegmentSeverity}%`,
      iconClassName: getCompareIconClassName(severity, avgSegmentSeverity),
    },
  ];

  return (
    <ScrollElement
      id={MENU_HASH.RELIABILITY}
      className={classnames('reliability px-1 py-1_5 bg-cool-gray-90 rounded-12', className)}
      data-tracking-parent={CREATIVE_ID}
    >
      <hgroup className="mb-1_5">
        <h2 className="heading-3 mb-0_5">Reliability</h2>
        <p className="size-16 mb-0">
          The {year} {makeName} {modelName} has {mmySummary}.{' '}
          {!!reliabilityScore5Scale && (
            <Fragment>
              RepairPal gives it a reliability rating of {reliabilityScore5Scale} out of 5, ranks it #
              {segmentRankByReliabilityScore} out of {modelCountOfSegment} among {getLastWordPlural(segment)}.{' '}
            </Fragment>
          )}
          {isUsed && <Fragment>Below you’ll find our owner reviews.</Fragment>}
        </p>
      </hgroup>
      <div className="d-flex flex-column gap-1">
        {!!reliabilityScore5Scale && (
          <section id={SECTION_ID.REPAIR_PAL} className="px-1_5 pt-1_5 pb-0_75 bg-white rounded-12">
            <h3 className="heading-4 mb-1_5">Reliability Rating by RepairPal</h3>
            <div className="d-flex flex-column flex-md-row align-items-md-end mb-1">
              <strong className="rating mb-0_25 mb-md-0 d-flex align-items-end">
                <RatingStars rating={reliabilityScore5Scale} className="text-nowrap text-blue-30 size-20 me-0_5" />
                <span className="rating-number heading-3">{reliabilityScore5Scale}</span>
                <span className="medium fw-normal me-0_5">/5</span>
                {gearScoreDescription}{' '}
              </strong>
              <div className="model-rank medium ps-md-0_5 ms-md-0_5">
                #{segmentRankByReliabilityScore} out of {modelCountOfSegment} among {getLastWordPlural(segment)}
              </div>
            </div>
            <ContentCollapse
              minHeight={'0'}
              textClosed="See Cost, Frequency, and Severity"
              textOpen="See less"
              isEclipseFade={false}
              classes="mb-1_5"
              btnClasses="size-16 text-primary-darker px-0_5 py-0 text-transform-none"
              btnTextClasses="fw-normal"
              btnContainerClasses="p-0"
              btnTrackingId="view_repairpal_detail"
              isSameExpandCollapseTracking
              isOnlyExpandTracking
              showBtnBelow
              justify=""
              resetParams={
                isMobile
                  ? {
                      offset: SCROLL_OFFSET,
                      duration: SCROLL_DURATION,
                      to: SECTION_ID.REPAIR_PAL,
                    }
                  : undefined
              }
            >
              <p className="size-16 mt-0_5 mb-1">
                RepairPal Reliability Ratings are based on the actual cost, frequency, and severity of unscheduled
                repairs and maintenance on make/model data for select 2008-2022 vehicles. The reliability of a specific
                vehicle may vary depending on its maintenance and driving history, model year, trim, and features.
              </p>
              <Row className="mb-0_5">
                {repairPalSections.map(({ label, tooltipText, value, compareValue, colClassName, iconClassName }) => (
                  <Col key={label} xs={12} md={4} className={classnames('mb-1 mb-md-0', colClassName)}>
                    <section className="rounded-12 bg-white py-1_5 px-1_5 pt-md-1">
                      <div className="d-flex align-items-center mb-1_5">
                        <div className="size-16 fw-bold text-cool-gray-30 mb-0">{label}</div>
                        <PopoverItem
                          placement="top"
                          openTrackingParams={{ creative_id: CREATIVE_ID, value: label }}
                          screenReaderTooltipLabel={label}
                        >
                          {tooltipText}
                        </PopoverItem>
                      </div>
                      <div className="text-center">
                        <div className="heading-4 mb-0_5">
                          {!!iconClassName && (
                            <i className={classnames('size-16 fw-normal me-0_5', iconClassName)} aria-hidden />
                          )}
                          {value}
                        </div>
                        <div className="medium text-cool-gray-40">
                          vs. {compareValue}
                          <br className="hidden-md-down" /> for Average {segment}
                        </div>
                      </div>
                    </section>
                  </Col>
                ))}
              </Row>
            </ContentCollapse>
            <p className="small mb-0 fst-italic text-cool-gray-40">
              powered by RepairPal
              <PopoverItem
                openTrackingParams={{ creative_id: CREATIVE_ID, value: 'powered by RepairPal' }}
                screenReaderTooltipLabel="RepairPal data"
              >
                Based on RepairPal reliability data as of 8/23/2023. Ratings are provided by RepairPal and Edmunds is
                not responsible for their accuracy.
              </PopoverItem>
            </p>
          </section>
        )}
        <section id={SECTION_ID.RECALLS} className="recalls-section px-1_5 py-1_5 bg-white rounded-12">
          <h3 className="heading-4 mb-1_5">Recalls</h3>
          <strong className={classnames('d-flex align-items-baseline', { 'mb-1': !!recallsCount })}>
            <i
              className={classnames('size-20 me-0_75', {
                'icon-checkmark text-green-40': !recallsCount,
                'icon-warning2 text-orange-40': recallsCount,
              })}
              aria-hidden
            />
            {recallsCount ? (
              <Fragment>
                <span className="size-24 me-0_25">{recallsCount}</span>&nbsp;
                {getPlural('recall', recallsCount)} found
              </Fragment>
            ) : (
              <Fragment>
                There are no recalls for the {year} {makeName} {modelName}
              </Fragment>
            )}
          </strong>
          {!!recallsCount && (
            <ContentCollapse
              minHeight={'0'}
              textClosed="Show all recalls"
              textOpen="See less"
              isEclipseFade={false}
              btnClasses="size-16 text-primary-darker px-0_5 py-0 text-transform-none"
              btnTextClasses="fw-normal"
              btnContainerClasses="p-0"
              btnTrackingId="view_recalls"
              isSameExpandCollapseTracking
              isOnlyExpandTracking
              showBtnBelow
              justify=""
              resetParams={{
                offset: SCROLL_OFFSET,
                duration: SCROLL_DURATION,
                to: SECTION_ID.RECALLS,
              }}
            >
              <p className="medium">
                Checking if a car has a recall is simple. Just type the car&apos;s VIN number into the NHTSA website (
                <Link className="text-underline" to="https://www.nhtsa.gov/recalls" rel="nofollow" target="_blank">
                  https://www.nhtsa.gov/recalls
                </Link>
                ). If there&apos;s an unrepaired recall, you&apos;ll see it there.
                <br />
                <br />
                If a recall shows up, just call your local car dealer. They&apos;ll know about the recall and will work
                with the car maker to fix it for free (if the recall happened within 10 years of the car being first
                sold).
              </p>
              <Accordion
                className="mb-1 mb-md-0 mt-md-0_5"
                tabClasses="py-1 px-0 fw-bold size-16 flex-row-reverse justify-content-between"
                headerClasses="text-gray-darker size-16 text-start fw-bold m-0 p-0"
                itemClasses={null}
                iconCollapsed="icon-arrow-down3"
                iconExpanded="icon-arrow-up3"
                chevronClasses="ms-0_75 medium text-blue-50"
                allowMultipleOpen
              >
                {recalls.map(({ id, componentDescription, ...recallData }) => (
                  <div
                    key={`accordion-${id}`}
                    id={id}
                    label={capitalize(componentDescription)}
                    trackingId="view_recall_detail"
                  >
                    <dl>
                      {RECALL_SECTIONS.map(
                        ({ label, key, connector, connectedKey, wrapperClassName }) =>
                          !!recallData[key] && (
                            <div key={key} className={wrapperClassName}>
                              <dt>{label}:&nbsp;</dt>
                              <dd className="mb-0">
                                {recallData[key]}
                                {connector}
                                {recallData?.[connectedKey]}
                              </dd>
                            </div>
                          )
                      )}
                    </dl>
                  </div>
                ))}
              </Accordion>
            </ContentCollapse>
          )}
        </section>
        {isAvailable(basicWarranty) && (
          <section id={SECTION_ID.WARRANTY} className="warranty-section px-1_5 py-1_5 bg-white rounded-12">
            <h3 className="heading-4 mb-1_5">Warranty</h3>
            <strong className={classnames('d-flex align-items-center', { 'mb-1': allWarranties?.length > 1 })}>
              <i className="icon-shield-check text-green-40 size-20 me-0_75" aria-hidden />
              <span>
                <ContentFragment componentToUse="span">
                  {basicWarrantyText.replace(/^(.*?)\s/, '<span class="size-24">$1</span> ')}
                </ContentFragment>{' '}
                <span className="fw-normal d-inline-block">Basic Warranty</span>
              </span>
            </strong>
            {allWarranties?.length > 1 && (
              <ContentCollapse
                minHeight={'0'}
                textClosed="Show all warranties"
                textOpen="See less"
                isEclipseFade={false}
                btnClasses="size-16 text-primary-darker px-0_5 py-0 text-transform-none"
                btnTextClasses="fw-normal"
                btnContainerClasses="p-0"
                btnTrackingId="view_warranties_detail"
                isSameExpandCollapseTracking
                isOnlyExpandTracking
                showBtnBelow
                justify=""
                resetParams={
                  isMobile
                    ? {
                        offset: SCROLL_OFFSET,
                        duration: SCROLL_DURATION,
                        to: SECTION_ID.WARRANTY,
                      }
                    : undefined
                }
              >
                <dl>
                  {allWarranties.map(({ name, value }) => {
                    const valueText = numberWithCommasForString(
                      value?.replace('yr.', 'years ')?.replace('mi.', 'miles')
                    );

                    return (
                      <div key={name} className="warranty-wrapper d-flex justify-content-between py-0_75">
                        <dt className="pe-1">{name} Warranty</dt>
                        <ContentFragment componentToUse="dd" classes="mb-0 text-end">
                          {isMobile ? valueText.replace(/\//, '/<br/>') : valueText}
                        </ContentFragment>
                      </div>
                    );
                  })}
                </dl>
              </ContentCollapse>
            )}
          </section>
        )}
      </div>
    </ScrollElement>
  );
}

ReliabilityUI.propTypes = {
  vehicleParams: PropTypes.shape({
    makeName: PropTypes.string,
    modelName: PropTypes.string,
    makeSlug: PropTypes.string,
    modelSlug: PropTypes.string,
    submodelSlug: PropTypes.string,
    year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    modelYearId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  className: PropTypes.string,
  reliabilityRatings: ReliabilityEntities.RepairPalRatings,
  styleFeatures: FeatureSpecsEntities.FeatureStyleEntity,
  recalls: PropTypes.arrayOf(MaintenanceEntities.Recall),
  isUsed: PropTypes.bool,
  isMobile: PropTypes.bool,
};

export const Reliability = connectToModel(ReliabilityUI, {
  reliabilityRatings: bindToPath(
    ({ vehicleParams: { makeSlug, modelSlug, year } }) => getRepairPalDataPath({ makeSlug, modelSlug, year }),
    ReliabilityModel
  ),
  styleFeatures: bindToPath(
    ({ vehicleParams: { makeSlug, modelSlug, submodelSlug, year } }) =>
      FeatureSpecsPaths.buildPartialFeaturesPath({ make: makeSlug, model: modelSlug, submodel: submodelSlug, year }),
    VehicleModel,
    stylesFeatures => stylesFeatures?.[0]
  ),
  recalls: bindToPath(
    ({ vehicleParams: { modelYearId } }) => MaintenancePaths.buildMaintenanceRecallsPath(modelYearId),
    MaintenanceModel
  ),
});
